/* BKRRevealPopover — klikbare uitleg van de BKR-berekening voor een groep.

   Toont de exacte cohort-verdeling (kinderen per leeftijdscohort × ratio),
   de wiskundige som en het afgeronde minimum PMs. Directie kan zo aan GGD
   uitleggen HOE een BKR-status tot stand komt.

   Props:
     bkr        — {status, formula, children_count, scheduled_staff,
                    required_staff, disclaimer}
     children   — optional trigger; defaults to a small info badge.
*/
import React from "react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Info } from "lucide-react";

const STATUS_TONE = {
  ok:      { pill: "bg-emerald-100 text-emerald-800 border-emerald-200", header: "border-emerald-300" },
  warning: { pill: "bg-amber-100 text-amber-800 border-amber-200",       header: "border-amber-300" },
  alert:   { pill: "bg-red-100 text-red-800 border-red-200",             header: "border-red-300" },
  unknown: { pill: "bg-neutral-100 text-neutral-700 border-neutral-200", header: "border-neutral-300" },
};

// Barkleur per status voor de mini-trend sparkline
const BAR_COLOR = {
  ok: "#059669",
  warning: "#d97706",
  alert: "#dc2626",
  unknown: "#d4d4d8",
};

function BKRTrendSparkline({ groupId, locationId, refDatum }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(false);
  React.useEffect(() => {
    if (!groupId && !locationId) return;
    let cancel = false;
    const params = { days: 7 };
    if (groupId) params.group_id = groupId;
    else if (locationId) params.location_id = locationId;
    if (refDatum) params.datum = refDatum;
    api.get("/bkr/trend", { params })
      .then(r => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setErr(true); });
    return () => { cancel = true; };
    // `api` is stabiele module-level import.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, locationId, refDatum]);

  if (err) return null;
  if (!data) {
    return (
      <div className="h-12 flex items-center justify-center text-[10px] text-neutral-400"
           data-testid="bkr-reveal-trend-loading">
        trend laden…
      </div>
    );
  }
  const rows = data.days || [];
  if (rows.length === 0) return null;
  // Bar height: schedule / required (capped 150%); als required=0 → toon "onbekend"
  const maxRatio = Math.max(1.5, ...rows.map(r => r.required_staff ? (r.scheduled_staff / r.required_staff) : 0));
  const barW = 100 / rows.length;
  return (
    <div data-testid="bkr-reveal-trend">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mb-1 flex items-center justify-between">
        <span>Trend laatste {rows.length} dagen</span>
        <span className="text-neutral-400 normal-case tracking-normal">sched/req</span>
      </div>
      <svg viewBox="0 0 100 48" className="w-full h-12" preserveAspectRatio="none">
        {/* 100%-referentielijn (required = scheduled) */}
        <line x1="0" y1={48 - (48 * (1 / maxRatio))} x2="100" y2={48 - (48 * (1 / maxRatio))}
              stroke="#d4d4d8" strokeWidth="0.4" strokeDasharray="1,1" />
        {rows.map((r, i) => {
          const req = r.required_staff || 0;
          const sch = r.scheduled_staff || 0;
          const ratio = req > 0 ? Math.min(maxRatio, sch / req) : 0;
          const h = req > 0 ? Math.max(2, (ratio / maxRatio) * 44) : 2;
          const x = i * barW + barW * 0.15;
          const w = barW * 0.7;
          const y = 48 - h;
          return (
            <g key={r.datum} data-testid={`bkr-trend-bar-${r.datum}`}>
              <title>{`${r.datum} · ${sch}/${req} PM · ${r.children_count} kind · ${r.status}`}</title>
              <rect x={x} y={y} width={w} height={h}
                    fill={BAR_COLOR[r.status] || BAR_COLOR.unknown}
                    rx="0.6" />
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[9px] text-neutral-500 mt-0.5 tabular-nums">
        {rows.map((r) => (
          <span key={r.datum} title={r.datum} className="flex-1 text-center">
            {r.datum?.slice(8, 10) /* dd */}
          </span>
        ))}
      </div>
    </div>
  );
}


export function BKRRevealPopover({ bkr, children, testid, onGroupSelect }) {
  if (!bkr) return children || null;
  const tone = STATUS_TONE[bkr.status] || STATUS_TONE.unknown;
  const formula = bkr.formula || {};
  const cohorts = formula.cohorts || [];
  const shortage = Math.max(0, (bkr.required_staff || 0) - (bkr.scheduled_staff || 0));

  return (
    <Popover>
      <PopoverTrigger asChild>
        {children || (
          <button
            type="button"
            className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${tone.pill} hover:brightness-95`}
            data-testid={testid || "bkr-reveal-trigger"}
            title="Klik voor de exacte BKR-rekensom"
          >
            <Info className="h-3 w-3" />
            uitleg
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0" data-testid="bkr-reveal-popover">
        <div className={`px-4 py-3 border-b-2 ${tone.header} bg-white`}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-neutral-900">
              BKR-onderbouwing — {formula.type || bkr.location_type || "KDV"}
            </div>
            <Badge className={tone.pill}>{bkr.status}</Badge>
          </div>
          <div className="text-[11px] text-neutral-500 mt-0.5">
            {bkr.datum && <>Datum {bkr.datum} · </>}bron: {formula.source || "SZW 1ratio.nl (indicatief)"}
          </div>
        </div>

        <div className="p-4 space-y-3 text-sm">
          {bkr.status === "unknown" && (
            <div className="text-neutral-600 text-xs italic"
                 data-testid="bkr-reveal-unknown">
              Geen rooster of presentie-evidence op deze dag —
              status niet berekenbaar.
            </div>
          )}

          {cohorts.length > 0 && (
            <div data-testid="bkr-reveal-cohorts">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mb-1">
                Kinderen per leeftijdscohort
                {(bkr.per_group || []).length > 1 && (
                  <span className="ml-1 text-neutral-400 normal-case tracking-normal">
                    (som over {bkr.per_group.length} groepen)
                  </span>
                )}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-neutral-500 border-b border-neutral-100">
                    <th className="py-1 font-normal">Cohort</th>
                    <th className="py-1 font-normal text-right">Aantal</th>
                    <th className="py-1 font-normal text-right">Ratio</th>
                    <th className="py-1 font-normal text-right">PM-eis</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((c) => (
                    <tr key={c.cohort} className="border-b border-neutral-100 last:border-0"
                        data-testid={`bkr-reveal-cohort-${c.cohort}`}>
                      <td className="py-1 text-neutral-800">{c.label}</td>
                      <td className="py-1 text-right tabular-nums font-medium">{c.count}</td>
                      <td className="py-1 text-right text-neutral-600 tabular-nums">{c.ratio}</td>
                      <td className="py-1 text-right text-neutral-700 tabular-nums">{c.contribution?.toFixed?.(3) ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {formula.unknown_age_count > 0 && (
                <div className="text-[10px] text-amber-700 mt-1">
                  ⚠ {formula.unknown_age_count} kind(eren) zonder geldige geboortedatum uitgesloten
                </div>
              )}
            </div>
          )}

          {/* Per-groep breakdown — alleen op locatie-totalen zichtbaar
              zodat directie kan uitleggen welke groep dominant is. Als
              `onGroupSelect` beschikbaar is, wordt elke rij klikbaar en
              springt die naar de plan-focus voor die groep. */}
          {(bkr.per_group || []).length > 0 && (
            <div data-testid="bkr-reveal-per-group">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mb-1">
                Verdeling per groep{onGroupSelect ? " · klik om te focussen" : ""}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-neutral-500 border-b border-neutral-100">
                    <th className="py-1 font-normal">Groep</th>
                    <th className="py-1 font-normal text-right">Kind</th>
                    <th className="py-1 font-normal text-right">PM</th>
                    <th className="py-1 font-normal text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bkr.per_group.map((g) => {
                    const gTone = g.status === "ok" ? "text-emerald-700"
                                : g.status === "warning" ? "text-amber-700"
                                : g.status === "alert" ? "text-red-700"
                                : "text-neutral-500";
                    const commonProps = {
                      "data-testid": `bkr-reveal-per-group-${g.group_id}`,
                      className: "border-b border-neutral-100 last:border-0" +
                                 (onGroupSelect ? " cursor-pointer hover:bg-neutral-50 transition-colors" : ""),
                    };
                    const cells = (
                      <>
                        <td className="py-1 text-neutral-800 truncate max-w-[140px]">
                          {onGroupSelect
                            ? <span className="underline decoration-dotted decoration-neutral-400">{g.group_name}</span>
                            : g.group_name}
                        </td>
                        <td className="py-1 text-right tabular-nums">{g.children_count}</td>
                        <td className="py-1 text-right tabular-nums">{g.scheduled_staff}/{g.required_staff}</td>
                        <td className={`py-1 text-right font-medium ${gTone}`}>
                          {g.status === "ok" ? "OK"
                            : g.status === "warning" ? "krap"
                            : g.status === "alert" ? "tekort"
                            : g.status}
                        </td>
                      </>
                    );
                    if (!onGroupSelect) {
                      return <tr key={g.group_id} {...commonProps}>{cells}</tr>;
                    }
                    return (
                      <tr
                        key={g.group_id}
                        {...commonProps}
                        onClick={() => onGroupSelect(g.group_id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onGroupSelect(g.group_id);
                          }
                        }}
                        title={`Focus op ${g.group_name} in plan-wizard`}
                      >
                        {cells}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {formula.formula && (
            <div className="rounded bg-neutral-50 border border-neutral-200 px-3 py-2"
                 data-testid="bkr-reveal-formula">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mb-1">
                Rekensom (som, afgerond omhoog)
              </div>
              <div className="text-sm font-mono text-neutral-900">{formula.formula}</div>
              <div className="text-[10px] text-neutral-500 mt-1">
                exacte som: <span className="font-mono">{formula.sum_exact}</span>
                {" → "}minimum PMs (⌈⌉): <span className="font-mono font-bold">{formula.sum_rounded_up}</span>
              </div>
            </div>
          )}

          {/* Trend — mini-sparkline laatste 7 dagen. Vraagt slechts data
              op bij expanderen (parent-popover); groep- én locatie-context
              ondersteund. */}
          {(bkr.group_id || bkr.location_id) && (
            <BKRTrendSparkline
              groupId={bkr.group_id}
              locationId={!bkr.group_id ? bkr.location_id : null}
              refDatum={bkr.datum}
            />
          )}

          <div className="grid grid-cols-3 gap-2 text-center border-t border-neutral-100 pt-3"
               data-testid="bkr-reveal-summary">
            <div>
              <div className="text-[10px] uppercase text-neutral-500">Kinderen</div>
              <div className="text-lg font-bold tabular-nums">{bkr.children_count ?? bkr.kinderen ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-neutral-500">PMs vereist</div>
              <div className="text-lg font-bold tabular-nums">{bkr.required_staff ?? bkr.vereist ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-neutral-500">PMs gepland</div>
              <div className="text-lg font-bold tabular-nums">{bkr.scheduled_staff ?? bkr.medewerkers ?? 0}</div>
            </div>
          </div>

          {shortage > 0 && (
            <div className="text-xs text-red-700 font-medium" data-testid="bkr-reveal-tekort">
              Tekort: {shortage} PM
            </div>
          )}

          {/* Compliance-suggestion: concrete tekst hoe je compliant wordt.
              Gebaseerd op cohort-mix: welk cohort dominant is + hoeveel PM
              erbij nodig. */}
          {(() => {
            const req = bkr.required_staff ?? bkr.vereist ?? 0;
            const sched = bkr.scheduled_staff ?? bkr.medewerkers ?? 0;
            const gap = req - sched;
            if (bkr.status === "unknown") return null;
            if (gap <= 0) {
              // Compliant. Suggest headroom als de marge groot is.
              const surplus = sched - req;
              if (surplus >= 1) {
                return (
                  <div className="rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800"
                       data-testid="bkr-reveal-suggestion-ok">
                    ✓ Compliant met {surplus} PM boven de norm — ruimte om
                    walk-ins of extra dagdelen op te vangen.
                  </div>
                );
              }
              return (
                <div className="rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800"
                     data-testid="bkr-reveal-suggestion-ok">
                  ✓ Precies op de norm — geen buffer bij plotselinge uitval
                  of walk-in.
                </div>
              );
            }
            // Onder BKR — suggereer concrete PM-aanvulling
            const dominantCohort = (formula.cohorts || [])
              .slice()
              .sort((a, b) => (b.count || 0) - (a.count || 0))[0];
            const dominantHint = dominantCohort
              ? ` Zwaarste cohort: ${dominantCohort.label} (${dominantCohort.count} kind · ${dominantCohort.ratio}).`
              : "";
            return (
              <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-900"
                   data-testid="bkr-reveal-suggestion-fix">
                <div className="font-semibold">
                  Voeg {gap} PM toe om compliant te worden.
                </div>
                <div className="text-red-700 mt-0.5">
                  {dominantHint} Of verplaats {" "}
                  <span className="font-medium">
                    {Math.min(bkr.children_count ?? bkr.kinderen ?? 0,
                              Math.max(1, Math.ceil(gap * (dominantCohort?.ratio_denominator || 5))))}
                  </span>{" "}
                  kind(eren) uit dit cohort naar een andere groep.
                </div>
              </div>
            );
          })()}

          {bkr.disclaimer && (
            <div className="text-[10px] text-neutral-500 border-t border-neutral-100 pt-2 italic">
              {bkr.disclaimer}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
